/**
 * Backend API Service for Store Hours
 * Replaces Firebase direct calls with backend API calls
 */

export interface DayHours {
  open: string; // "09:00" format
  close: string; // "21:00" format
  closed: boolean;
}

export interface WeeklyHours {
  monday: DayHours;
  tuesday: DayHours;
  wednesday: DayHours;
  thursday: DayHours;
  friday: DayHours;
  saturday: DayHours;
  sunday: DayHours;
}

export interface TemporaryClosure {
  date: Date;
  reason: string;
  approved: boolean;
  // Optional special hours - if set, store is open with modified hours instead of closed
  specialHours?: {
    open: string;  // "09:00" format
    close: string; // "17:00" format
  };
}

export interface StoreHoursData {
  storeId: string;
  storeName: string;
  hours: WeeklyHours;
  lastEdited: Date | null;
  lastAuthor: string;
  editors: string[];
  temporaryClosures: TemporaryClosure[];
}

// Store mapping for consistent IDs and names - unified per physical location
export const STORE_LOCATIONS = [
  { id: 'stamford', name: 'Stamford, CT' },
  { id: 'west_hartford', name: 'West Hartford, CT' },
  { id: 'olney', name: 'Olney, MD' },
  { id: 'voorhees', name: 'Voorhees, NJ' },
  { id: 'river_edge', name: 'River Edge, NJ' },
  { id: 'mount_olive', name: 'Mt Olive, NJ' },
  { id: 'hamilton', name: 'Hamilton, NJ' },
  { id: 'exeter', name: 'Exeter, RI' },
  { id: 'essex_junction', name: 'Essex Junction, VT' }
];

// Map Dutchie menu IDs to unified store hour document IDs
const DUTCHIE_TO_STORE_ID_MAP: Record<string, string> = {
  // Stamford - both med and rec point to same unified document
  '6158': 'stamford',
  '6159': 'stamford',
  // West Hartford - both med and rec point to same unified document
  '5796': 'west_hartford',
  '5797': 'west_hartford',
  // Olney - both med and rec point to same unified document
  '857': 'olney',
  '5440': 'olney',
  // Voorhees - both med and rec point to same unified document
  '4855': 'voorhees',
  '5377': 'voorhees',
  // River Edge - rec only
  '6247': 'river_edge',
  // Mt Olive - rec only
  '6248': 'mount_olive',
  // Hamilton - rec only
  '696002abeb54a2091b709e0f': 'hamilton',
  // Exeter - both med and rec point to same unified document
  '4545': 'exeter',
  '4996': 'exeter',
  // Essex Junction - rec only
  '6145': 'essex_junction'
};

/**
 * Convert Dutchie menu ID to internal store ID
 */
const mapDutchieIdToStoreId = (dutchieId: string): string => {
  return DUTCHIE_TO_STORE_ID_MAP[dutchieId] || dutchieId;
};

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL!;

/**
 * Get Firebase ID token from current user
 * Requires Firebase Auth to be initialized and user logged in
 */
const getAuthToken = async (): Promise<string> => {
  const { getAuth } = await import('firebase/auth');
  const auth = getAuth();
  if (!auth.currentUser) throw new Error('No authenticated user found');
  return await auth.currentUser.getIdToken();
};

/**
 * Get store hours (public endpoint)
 */
export const getStoreHours = async (storeId: string): Promise<StoreHoursData> => {
  try {
    // Map Dutchie ID to internal store ID
    const mappedStoreId = mapDutchieIdToStoreId(storeId);
    
    const response = await fetch(`${API_BASE_URL}/api/store-hours/${mappedStoreId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    if (result.success && result.data) {
      return {
        ...result.data,
        lastEdited: result.data.lastEdited ? new Date(result.data.lastEdited) : null,
        temporaryClosures: result.data.temporaryClosures?.map((closure: any) => ({
          ...closure,
          date: new Date(closure.date)
        })) || []
      };
    }

    throw new Error('Invalid response format from store hours API');
  } catch (error) {
    console.error('❌ Error fetching store hours from backend:', error);
    throw error;
  }
};

/**
 * Get store hours for admin editing (requires authentication)
 */
export const getStoreHoursForAdmin = async (storeId: string): Promise<StoreHoursData> => {
  try {
    // Map Dutchie ID to internal store ID
    const mappedStoreId = mapDutchieIdToStoreId(storeId);
    
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/api/store-hours/${mappedStoreId}/admin`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    if (result.success && result.data) {
      return {
        ...result.data,
        lastEdited: result.data.lastEdited ? new Date(result.data.lastEdited) : null,
        temporaryClosures: result.data.temporaryClosures?.map((closure: any) => ({
          ...closure,
          date: new Date(closure.date)
        })) || []
      };
    }

    throw new Error('Invalid response format from store hours admin API');
  } catch (error) {
    console.error('❌ Error fetching store hours (admin) from backend:', error);
    throw error;
  }
};

/**
 * Save store hours (requires authentication)
 */
export const saveStoreHours = async (
  storeId: string,
  hours: WeeklyHours
): Promise<void> => {
  try {
    // Map Dutchie ID to internal store ID
    const mappedStoreId = mapDutchieIdToStoreId(storeId);
    
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/api/store-hours/${mappedStoreId}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        hours,
      }),
    });

    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    if (!result.success) {
      throw new Error(result.error || 'Failed to save store hours');
    }
  } catch (error) {
    console.error('❌ Error saving store hours to backend:', error);
    throw error;
  }
};

/**
 * Get all store locations (public endpoint)
 */
/**
 * Update temporary closures for a specific store (requires authentication)
 */
export const updateTemporaryClosures = async (
  storeId: string,
  temporaryClosures: TemporaryClosure[]
): Promise<void> => {
  try {
    const mappedStoreId = mapDutchieIdToStoreId(storeId);
    
    const token = await getAuthToken();
    
    // Convert dates to ISO strings for API
    const closuresForApi = temporaryClosures.map(closure => ({
      ...closure,
      date: closure.date.toISOString()
    }));
    
    const requestBody = {
      temporaryClosures: closuresForApi,
    };
    
    const response = await fetch(`${API_BASE_URL}/api/store-hours/${mappedStoreId}/temporary-closures`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    if (!result.success) {
      throw new Error(result.error || 'Failed to update temporary closures');
    }
  } catch (error) {
    console.error('💥 Error in updateTemporaryClosures API call:', error);
    throw error;
  }
};

/**
 * Add federal holidays to a specific store (requires authentication)
 */
export const addHolidaysToStore = async (storeId: string): Promise<{ holidaysAdded: number; updatedData: StoreHoursData }> => {
  try {
    const mappedStoreId = mapDutchieIdToStoreId(storeId);
    
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/api/store-hours/${mappedStoreId}/add-holidays`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    if (!result.success) {
      throw new Error(result.error || 'Failed to add holidays');
    }
    
    // Process the returned store data
    const processedData = {
      ...result.data,
      lastEdited: result.data.lastEdited ? new Date(result.data.lastEdited) : null,
      temporaryClosures: result.data.temporaryClosures?.map((closure: any) => ({
        ...closure,
        date: new Date(closure.date)
      })) || []
    };
    
    return {
      holidaysAdded: result.holidaysAdded,
      updatedData: processedData
    };
  } catch (error) {
    console.error('❌ Error adding holidays to store:', error);
    throw error;
  }
};

/**
 * Trigger holiday update for all stores (requires authentication)
 */
export const updateHolidaysForAllStores = async (): Promise<void> => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/api/store-hours/update-holidays`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    if (!result.success) {
      throw new Error(result.error || 'Failed to update holidays');
    }
  } catch (error) {
    console.error('❌ Error updating holidays:', error);
    throw error;
  }
};

export const getStoreLocations = async (): Promise<Array<{ id: string; name: string }>> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/store-hours/locations`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    if (result.success && result.data) {
      return result.data;
    }

    throw new Error('Invalid response format from store locations API');
  } catch (error) {
    console.error('❌ Error fetching store locations from backend:', error);
    throw error;
  }
};